// @ts-nocheck
import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { find, get } from 'lodash'
import Actions from '../../../actions'
import Operations from '../../../operations'
import Selectors from '../../../selectors'
import { postJSON } from '../../../common/services/api'
import { sizes } from '@lmc/common/constants'
import { getNominative } from '../../../common/utils'

import { Spinner, Step, StepList } from '../../generic'
import { ScrollLock } from '../../../LEGACY/elemental'
import Modal from '..'
import RiskAssessmentForm from '../../forms/screens/RiskAssessmentForm'
import {
  AssessmentTitleScreen,
  AssessmentQuestionScreen,
  CreateAssessmentDashboard,
} from './components'
import { withRouter } from '../../../withRouter'
import useRiskAssessmentData from './hooks/useRiskAssessmentData'

/**
 * Two use cases for CreateRiskAssessment:
 * 1. Updating an existing riskAssessment (props.completedRiskAssessment === { RiskAssessment })
 *    Skip straight to renderEditForm
 *    There is a problem with assessments which filled out forms that have since been edited.
 *    Forms where `isRevision === true` are not included in `loadList('forms')`, so they need
 *    to be fetched individually
 * 2. Filling out a Blank Risk Assessment (props.completedRiskAssessment === null)
 *    Render a step list, requiring a list of all available forms from `loadList('forms')`
 */
export const CreateRiskAssessment = (props) => {
  const {
    isOpen,
    loadList,
    completedRiskAssessment,
    router: { params },
    parentFormId,
    addRiskAssessment,
    setLoadedListItem,
    showToast,
    close,
    attachToCarePlan,
    carers,
    formsAndAssessments,
    carePlanNominative,
    preferredPeopleNominative,
    router,
  } = props

  const location = useLocation()
  const [addCustom, setAddCustom] = useState(false)
  const [addExisting, setAddExisting] = useState(false)
  const [customName, setCustomName] = useState('')
  const [existingRiskAssessment, setExistingRiskAssessment] = useState(null)
  const [riskAssessmentFormId, setRiskAssessmentFormId] = useState(null)
  const [parentFormIdState, setParentFormIdState] = useState(null)
  const [pendingStepAdvance, setPendingStepAdvance] = useState(null)

  const riskAssessmentId =
    params?.risk_assessment_id || params?.risk_assessment_history_id

  const {
    riskAssessment: fetchedRiskAssessment,
    isLoading: isLoadingRiskAssessment,
    error: riskAssessmentError,
  } = useRiskAssessmentData(
    riskAssessmentId && !completedRiskAssessment ? riskAssessmentId : undefined,
  )

  const currentRiskAssessment = completedRiskAssessment || fetchedRiskAssessment

  useEffect(() => {
    if (!parentFormIdState && parentFormId) {
      setParentFormIdState(parentFormId)
    }

    const residentId = params?.id

    if (!currentRiskAssessment && isOpen && params && residentId) {
      loadList('risk-assessments', {
        mostRecent: true,
        resident: residentId,
        withFormData: true,
      })
      loadList('forms', {
        type: 'risk-assessment',
      })
    }
  }, [
    isOpen,
    currentRiskAssessment,
    params,
    parentFormId,
    parentFormIdState,
    loadList,
  ])

  useEffect(() => {
    if (
      pendingStepAdvance &&
      (addCustom || addExisting || riskAssessmentFormId)
    ) {
      pendingStepAdvance()
      setPendingStepAdvance(null)
    }
  }, [pendingStepAdvance, addCustom, addExisting, riskAssessmentFormId])

  const onFormClose = () => {
    setExistingRiskAssessment(null)
    setRiskAssessmentFormId(null)
    setPendingStepAdvance(null)
    close()
  }

  const onComplete = (item) => {
    setLoadedListItem('risk-assessments', item)
    showToast({ title: 'Complete' })
    if (parentFormIdState) {
      addRiskAssessment(item.id, parentFormIdState)
    }
    onFormClose()
  }

  const handleAddNew = (id, isCustom, callback, preserveCustomStep = false) => {
    if (!preserveCustomStep) {
      setAddCustom(isCustom || false)
      setCustomName('')
    }
    setAddExisting(false)
    setExistingRiskAssessment(null)
    setRiskAssessmentFormId(id)

    if (callback) {
      setPendingStepAdvance(() => callback)
    }
  }

  const handleAddExisting = (
    existingRiskAssessment,
    callback,
    preserveCustomStep = false,
  ) => {
    if (!preserveCustomStep) {
      setAddCustom(false)
      setCustomName('')
    }
    setAddExisting(true)
    setExistingRiskAssessment(existingRiskAssessment)
    setRiskAssessmentFormId(existingRiskAssessment.form.id)

    if (callback) {
      setPendingStepAdvance(() => callback)
    }
  }

  const handleEditAssessment = () => {
    const residentId = router.params.id
    const riskAssessmentId = existingRiskAssessment?.id

    if (residentId && riskAssessmentId) {
      close()
      router.navigate(
        `/residents/${residentId}/risk-assessments/${riskAssessmentId}?edit=true`,
      )
    }
  }

  const handleSimilarAddExisting = (existingRiskAssessment, callback) => {
    handleAddExisting(existingRiskAssessment, callback, true)
  }

  const handleSimilarAddNew = (id, isCustom, callback) => {
    handleAddNew(id, isCustom, callback, true)
  }

  const handleAttachBlank = () => {
    setAddExisting(false)
    setCustomName('')
    setExistingRiskAssessment(null)
  }

  const handleCreateNewBlankAssessment = () => {
    setAddExisting(false)
    setExistingRiskAssessment(null)
    if (pendingStepAdvance) {
      pendingStepAdvance()
      setPendingStepAdvance(null)
    }
  }

  const handleCustomNameChange = (customName) => setCustomName(customName)

  const handleCompleteLater = (item) => {
    if (!item) return alert(INTERNAL_ERROR)

    const isExisting = isExistingAssessment(item)
    const id = isExisting ? item.form.id : item.id

    if (addExisting) {
      const assessmentItem = isExisting ? item : findItem(item.id)
      onComplete(assessmentItem)
    } else {
      let name
      if (customName) {
        const parentFormId = isExisting ? item.form.name : item.name
        name = `${parentFormId} for ${customName}`
      }
      const json = {
        form: id,
        formData: {},
        incomplete: true,
        name,
        resident: router.params.id,
      }
      postJSON({
        url: 'list/risk-assessments',
        method: 'POST',
        json,
      })
        .then((res) => {
          onComplete(res.result)
        })
        .catch((e) => {
          console.log('Error:', e)
          alert(e)
        })
    }
  }

  const findItem = (id) =>
    find(formsAndAssessments, (item) => {
      if (item.form && item.form.id) {
        return id === item.form.id
      }
      return id === item.id
    })

  const isCompletedAssessment = () => {
    return existingRiskAssessment && !existingRiskAssessment.incomplete
  }

  const isExistingAssessment = (assessment) =>
    assessment.form && assessment.form.id

  const renderEditForm = () => {
    const displayCarePlanIfAdlRecordExists = attachToCarePlan?.carePlanAdl
      ?.name && [attachToCarePlan?.carePlanAdl]

    return (
      <StepList
        displayDots={false}
        displaySubmit={false}
        displaySelection={false}
        onClose={close}
        closeLabel="Cancel"
        stepStyle={styles.step}
      >
        <Step
          component={RiskAssessmentForm}
          carePlansDefault={
            // We need eitherways existing completedRiskAssessment or attached care plans from the form
            currentRiskAssessment.carePlans || displayCarePlanIfAdlRecordExists
          }
          form_id={currentRiskAssessment.form.id}
          formData={currentRiskAssessment.formData}
          isCustom={addCustom}
          isModal
          onComplete={onComplete}
          onCancel={onFormClose}
          router={router}
          riskAssessmentId={currentRiskAssessment.id}
          archived={currentRiskAssessment.archived}
        />
      </StepList>
    )
  }

  const renderFormStep = () => {
    const form_id = existingRiskAssessment
      ? existingRiskAssessment.form.id
      : riskAssessmentFormId

    const formData = existingRiskAssessment
      ? existingRiskAssessment.formData
      : null

    const riskAssessmentId = existingRiskAssessment
      ? existingRiskAssessment.id
      : null

    const displayCarePlanIfAdlRecordExists = attachToCarePlan?.carePlanAdl
      ?.name && [attachToCarePlan?.carePlanAdl]

    return (
      <Step
        component={RiskAssessmentForm}
        // We need eitherways existing existingRiskAssessment or attached care plans from the form
        carePlansDefault={
          (existingRiskAssessment ? existingRiskAssessment?.carePlans : null) ||
          displayCarePlanIfAdlRecordExists
        }
        form_id={form_id}
        formData={formData}
        customName={customName}
        isModal
        onComplete={onComplete}
        onCancel={onFormClose}
        router={router}
        riskAssessmentId={riskAssessmentId}
      />
    )
  }

  const renderStepList = () => {
    const { params, ...formProps } = props

    const currentItem = findItem(riskAssessmentFormId)

    let form
    if (currentItem) form = currentItem.form || currentItem

    const carePlansToAttach = params?.care_plan_id
    const isInCarePlanEdit =
      location.pathname.includes('/care-plans/') &&
      location.pathname.includes('/edit')

    const completedAssessmentOptions = isInCarePlanEdit
      ? ATTACH_COMPLETED_ASSESSMENT_OPTIONS
      : ALREADY_COMPLETED_OPTIONS

    const onSecondaryActionHandler = isInCarePlanEdit
      ? handleCompleteLater
      : handleEditAssessment

    return (
      <StepList
        displaySelection={false}
        displaySubmit={false}
        onClose={close}
        closeLabel="Cancel"
        stepStyle={styles.step}
      >
        <Step
          component={CreateAssessmentDashboard}
          data={{ carers, formsAndAssessments, carePlanNominative }}
          addNew={handleAddNew}
          addExisting={handleAddExisting}
          {...formProps}
        />

        {addCustom && (
          <Step
            component={AssessmentTitleScreen}
            form={form}
            onChange={handleCustomNameChange}
            value={customName}
            formsAndAssessments={formsAndAssessments}
            carers={carers}
            carePlanNominative={carePlanNominative}
            addExisting={handleSimilarAddExisting}
            addNew={handleSimilarAddNew}
            router={router}
            {...formProps}
          />
        )}
        {addExisting && isCompletedAssessment() && (
          <Step
            component={AssessmentQuestionScreen}
            continueOnPrimaryAction
            form={form}
            note={getAlreadyCompletedNote(preferredPeopleNominative)}
            onPrimaryAction={handleCreateNewBlankAssessment}
            onSecondaryAction={onSecondaryActionHandler}
            options={completedAssessmentOptions}
            primaryButtonText="Next Step"
            question={getAlreadyCompletedQuestion(preferredPeopleNominative)}
            {...formProps}
          />
        )}
        {carePlansToAttach && !addExisting && (
          <Step
            component={AssessmentQuestionScreen}
            form={form}
            note={getAttachAssessmentNote(carePlanNominative)}
            onPrimaryAction={handleCompleteLater}
            options={ATTACH_ASSESSMENT_OPTIONS}
            question={ATTACH_ASSESSMENT_QUESTION}
            {...formProps}
          />
        )}
        {renderFormStep()}
      </StepList>
    )
  }

  if (!isOpen || !carers || !router || !router.params) return null

  if (isLoadingRiskAssessment) return <Spinner />

  if (riskAssessmentError) {
    console.error('Error loading risk assessment:', riskAssessmentError)
    return <div>Error loading risk assessment</div>
  }

  if (!currentRiskAssessment && !formsAndAssessments) return <Spinner />

  return (
    <div style={styles.container}>
      <div style={styles.formContainer}>
        {currentRiskAssessment ? renderEditForm() : renderStepList()}
      </div>
      <ScrollLock />
    </div>
  )
}

const getAlreadyCompletedNote = (preferredPeopleNominative) =>
  `You can choose to edit the already completed Assessment or fill out a new blank Assessment. If you choose to start a blank Assessment, this will overwrite the previously completed Assessment for this ${getNominative(
    preferredPeopleNominative,
    'Resident',
  )}.`
const getAlreadyCompletedQuestion = (preferredPeopleNominative) =>
  `This Assessment has already been completed for this ${getNominative(
    preferredPeopleNominative,
    'Resident',
  )}`
const getAttachAssessmentNote = (carePlanNominative) =>
  `This Assessment will be attached to the ${getNominative(
    carePlanNominative,
    'Care Plan',
  )}. You can choose to fill it out now or later.`
const ALREADY_COMPLETED_OPTIONS = [
  { value: 'Edit the existing assessment' },
  { value: 'Fill out a new blank assessment', primary: true },
]
const ATTACH_COMPLETED_ASSESSMENT_OPTIONS = [
  { value: 'Attach the completed assessment' },
  { value: 'Fill out a new blank assessment', primary: true },
]
const ATTACH_ASSESSMENT_OPTIONS = [
  { value: 'Now' },
  { value: 'Later', primary: true },
]
const ATTACH_ASSESSMENT_QUESTION =
  'Would you like to complete this Assessment now or later?'

const FORM_ID = 'createRiskAssessment'
const INTERNAL_ERROR =
  'Oops! Something went wrong. Please refresh the page and try again.'

const styles = {
  container: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    margin: '0 auto',
    zIndex: 100,
    background: 'white',
  },
  formContainer: {
    height: '100vh',
    padding: '12px 12px 0px',
    overflow: 'auto',
  },
  step: {
    maxWidth: sizes.childScreen,
    margin: '0 auto',
    padding: '20px 20px 50px',
  },
}

CreateRiskAssessment.propTypes = {
  addRiskAssessment: PropTypes.func.isRequired,
  carePlanFormData: PropTypes.object,
  carers: PropTypes.array,
  parentFormId: PropTypes.string,
  formsAndAssessments: PropTypes.array,
  loadList: PropTypes.func.isRequired,
  setLoadedListItem: PropTypes.func.isRequired,
}

const mapStateToProps = (state, props) => ({
  carers: state.data.carers,
  carePlanFormData: get(state, `forms.${props.parentFormId}.fields`),
  formsAndAssessments: Selectors.concatenateFormsAndAssessments(state),
  carePlanNominative: state.global.home.preferredCarePlansNominative,
  preferredPeopleNominative: state.global.home.preferredPeopleNominative,
})

const mapDispatchToProps = (dispatch) => ({
  setLoadedListItem: (list_id, item) =>
    dispatch(Actions.setLoadedListItem(list_id, item)),
  addRiskAssessment: (id, formId) =>
    dispatch(Operations.addCarePlanRiskAssessment(id, formId)),
  loadList: (list_id, query) => dispatch(Operations.loadList(list_id, query)),
})

const wrappedModal = connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter(CreateRiskAssessment))

export default Modal(wrappedModal, FORM_ID)
